import { useCallback } from 'react';
import { Icon, Loader } from '@treezor/design-system';
import { useQuery } from '@tanstack/react-query';

import { createHttpHeader } from '@/utils/network/createHTTPHeaders';

interface IUseAsyncInput<T> {
  key: string;
  staleTime?: number;
  inputValue: string;
  url: string;
  handleResponse: (response: object) => T[];
  shouldSkip?: boolean;
}

export function useAsyncInput<T>({
  key,
  staleTime = 120000,
  inputValue,
  url,
  handleResponse,
  shouldSkip,
}: IUseAsyncInput<T>) {
  const headers = createHttpHeader();

  const { data, isLoading, isError, isSuccess } = useQuery({
    queryKey: [key, inputValue],
    queryFn: async () => {
      const response = await fetch(url, {
        headers,
      });

      if (!response.ok) {
        throw response;
      }

      const jsonData = await response.json();
      return handleResponse(jsonData);
    },
    enabled: !!inputValue && !shouldSkip,
    staleTime,
  });

  const hasError = isError || data?.length === 0;

  const getStatus = useCallback(() => {
    if (hasError) {
      return 'error';
    }

    if (isLoading) {
      return 'pending';
    }

    if (isSuccess) {
      return 'success';
    }
    return '';
  }, [isSuccess, hasError, isLoading]);

  const getIconStatus = useCallback(() => {
    const status = getStatus();

    if (status === 'success') {
      return <Icon name="check_circle" size="s" isFill={false} className="text-icon-success" />;
    }

    if (status === 'pending') {
      return (
        <div className="flex items-center justify-center">
          <Loader className="w-[18px] h-[18px] text-icon-disabled bg-transparent pointer-events-none" />
        </div>
      );
    }

    return null;
  }, [getStatus]);

  return { data, isLoading, hasError, status: getStatus(), iconStatus: getIconStatus() };
}
